import { defineStore } from 'pinia';

import { api } from './api.js';
import { pinia } from './store.js';

// Данные проекта, общие для его вкладок.
//
// Правило простое: если значение видят двое — оно живёт здесь; если одно —
// остаётся в компоненте. Раньше правила не было, и одно и то же лежало в двух
// местах сразу. Счётчик повторов приезжал и в шапку проекта, и во вкладку
// карточек: ответ на карточку менял цифру во вкладке, а в шапке она оставалась
// прежней до перезагрузки страницы. Слабые темы грузились дважды — на главной
// вкладке и в прогрессе, разными запросами.
//
// Формы, переключатели и «занято» сюда не идут: это состояние экрана, а не
// проекта, и общим ему быть незачем.

export const useProject = defineStore('project', {
  state: () => ({
    id: null,
    info: null,          // сама карточка проекта: направление, уровень, агент
    context: '',
    files: [],
    briefing: null,
    weak: [],            // слабые темы: нужны и на старте собеса, и в прогрессе
    sessions: [],
    sessionsLoaded: false,
    // Список собесов — страницами и с фильтром по формату: у того, кто ходит
    // сюда неделями, их сотня, и одним куском это не читается.
    sessionKind: '',
    sessionPage: 1,
    sessionPages: 1,
    sessionTotal: 0,
    sessionShown: 0,
    sessionCounts: {},
    due: 0,              // сколько карточек ждёт повтора
    loading: true,
    error: '',
  }),
  actions: {

    /** Сколько карточек ждёт повтора. Меняется после каждого ответа в повторах. */
    setDue(n) {
      this.due = Number(n) || 0;
    },

    /**
     * Всё про проект одним заходом.
     *
     * Два запроса разом, а не по очереди: список собесов приходит вторым, и при
     * последовательной загрузке страница успевала показать «собесов пока нет» у
     * проекта, где их шестнадцать.
     */
    /**
     * Забыть чужое перед загрузкой другого проекта.
     *
     * Стор переживает уход со страницы — в этом и смысл. Но данные прошлого
     * проекта на экране нового — это не «пока грузится», это неправда:
     * человек долю секунды видит чужое имя, чужие материалы и чужой счётчик
     * повторов. Возвращение в тот же проект при этом ничего не стирает —
     * иначе экран моргает на ровном месте.
     */
    open(id) {
      if (this.id === id) return;
      this.id = id;
      this.info = null;
      this.context = '';
      this.files = [];
      this.briefing = null;
      this.weak = [];
      this.sessions = [];
      this.sessionsLoaded = false;
      this.sessionKind = '';
      this.sessionPage = 1;
      this.sessionPages = 1;
      this.sessionTotal = 0;
      this.sessionShown = 0;
      this.sessionCounts = {};
      this.due = 0;
      this.error = '';
    },

    async load(id) {
      this.open(id);
      this.loading = true;
      this.error = '';
      try {
        const [data, s] = await Promise.all([
          api.get(`/api/projects/${id}`),
          api.get(`/api/projects/${id}/sessions?page=1`),
        ]);
        this.id = id;
        this.info = data.project;
        this.context = data.context;
        this.files = data.files;
        this.briefing = data.briefing;
        this.weak = data.weak || [];
        this.setDue(data.due);
        this.takeSessions(s);
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },

    /** Разложить страницу списка: строки и всё, что нужно фильтрам и пагинации. */
    takeSessions(data) {
      this.sessions = data.rows || [];
      this.sessionCounts = data.counts || {};
      this.sessionTotal = data.total || 0;
      this.sessionShown = data.shown || 0;
      this.sessionPage = data.page || 1;
      this.sessionPages = data.pages || 1;
      this.sessionsLoaded = true;
    },

    /**
     * Другая страница или другой формат.
     *
     * Ходим на сервер, а не режем список на клиенте: у активного пользователя
     * собесов сотни, и тянуть их все ради одной страницы бессмысленно.
     */
    async openSessions({ kind = this.sessionKind, page = 1 } = {}) {
      if (!this.id) return;
      this.sessionKind = kind;
      const q = new URLSearchParams({ page: String(page), kind });
      const data = await api.get(`/api/projects/${this.id}/sessions?${q}`).catch(() => null);
      if (data) this.takeSessions(data);
    },

    /** Перечитать только материалы: их правят на той же странице. */
    async refreshFiles() {
      if (!this.id) return;
      const data = await api.get(`/api/projects/${this.id}`).catch(() => null);
      if (!data) return;
      this.files = data.files;
      this.briefing = data.briefing;
      this.context = data.context;
    },
  },
});

// Готовый экземпляр рядом со стором: за состоянием ходят и обычные модули.
export const project = useProject(pinia);
export const setDue = (n) => project.setDue(n);
export const loadProject = (id) => project.load(id);
export const openProject = (id) => project.open(id);
export const refreshFiles = () => project.refreshFiles();
export const openSessions = (opts) => project.openSessions(opts);
